import type { Workflow } from './workflow';

/**
 * Workflow persistence.
 *
 * localStorage for the prototype — enough to make "Your workflows" real and to
 * demonstrate that a workflow is a saved, re-runnable object. The shape is the
 * same one a server would store, so moving this to an API later is a swap of
 * these four functions and nothing else.
 */

const KEY = 'streaming-economics.workflows';

function readAll(): Workflow[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // A corrupt entry shouldn't take the page down — start clean instead.
    return [];
  }
}

function writeAll(workflows: Workflow[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(workflows));
  } catch {
    // Quota or private-mode failures are non-fatal; the canvas still works.
  }
}

/** Most recently updated first. */
export function listWorkflows(): Workflow[] {
  return readAll().sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function getWorkflow(id: string): Workflow | undefined {
  return readAll().find((w) => w.id === id);
}

export function saveWorkflow(workflow: Workflow): void {
  const all = readAll();
  const index = all.findIndex((w) => w.id === workflow.id);
  const next = { ...workflow, updatedAt: new Date().toISOString() };
  if (index >= 0) all[index] = next;
  else all.push(next);
  writeAll(all);
}

export function deleteWorkflow(id: string): void {
  writeAll(readAll().filter((w) => w.id !== id));
}
